import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../App";
import usePushNotifications from "../hooks/usePushNotifications";
import { toast } from "sonner";

const DISMISS_KEY = "bt_push_banner_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function EnableNotificationsBanner() {
  const { token } = useAuth();
  const { supported, permission, isSubscribed, loading, subscribe } = usePushNotifications(token);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (!supported) {
      setHidden(true);
      return;
    }
    if (isSubscribed) {
      setHidden(true);
      return;
    }
    if (permission === "denied") {
      setHidden(true);
      return;
    }
    // Honour recent dismissal
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && Date.now() - ts < DISMISS_TTL_MS) {
        setHidden(true);
        return;
      }
    } catch (e) {
      // ignore
    }
    setHidden(false);
  }, [supported, permission, isSubscribed]);

  const handleEnable = async () => {
    const res = await subscribe();
    if (res.ok) {
      toast.success("Notifications enabled");
      setHidden(true);
    } else if (res.reason === "permission-denied") {
      toast.error("Notification permission denied. Re-enable in your browser settings.");
      setHidden(true);
    } else {
      toast.error("Could not enable notifications");
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      // ignore
    }
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <div
      className="mb-4 sm:mb-6 rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-purple-50 p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
      data-testid="enable-notifications-banner"
    >
      <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
          <Bell className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 text-sm sm:text-base">
            Stay in the loop
          </p>
          <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
            Get a notification the moment your test, quiz, or notes are ready — even when the app is closed.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          onClick={handleEnable}
          disabled={loading}
          className="btn-primary gap-2"
          data-testid="enable-notifications-btn"
        >
          <Bell className="w-4 h-4" />
          {loading ? "Enabling..." : "Enable"}
        </Button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          data-testid="dismiss-notifications-banner-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
